import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Dialog, Popover, Tabs, TextField, toastManager } from '@trove/ui';
import type {
  AuditEvent,
  PasskeyCredential,
  ProfileIconBackgroundId,
  ProfileIconId,
  SaveMode,
  StorageUsage,
} from '@trove/contracts';
import {
  Pulse,
  PencilSimple,
  Plus,
  Rows,
  ShieldCheck,
  List,
  TerminalWindow,
  Trash,
} from '@phosphor-icons/react';
import { api, putJson } from '../api';
import { PASSKEY_ICON_OPTIONS, passkeyIconComponent, type PasskeyIcon } from '../passkeyIcons';
import {
  enablePasskeyUnlock,
  passkeysSupported,
  registerPasskey,
  revokePasskey,
} from '../passkeys';
import { loadFileListDensity, saveFileListDensity, type FileListDensity } from '../preferences';
import { PROFILE_ICON_BACKGROUND_OPTIONS, PROFILE_ICON_OPTIONS } from '../profileIcons';
import { useSession } from '../session';
import { useWorkspace } from '../workspace';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { RelativeTime } from '../components/RelativeTime';
import { StorageMeter } from '../components/StorageMeter';
import shared from '../styles/shared.module.css';
import styles from './settings.module.css';

export const Route = createFileRoute('/settings')({ component: SettingsPage });

interface Device {
  id: string;
  label: string;
  createdAt: string;
  lastSeenAt: string;
}

function SettingsPage() {
  const { user } = useSession();
  const navigate = useNavigate();
  const { workspace, saveMode, setSaveMode, dirty } = useWorkspace();
  const queryClient = useQueryClient();
  const [density, setDensityState] = useState<FileListDensity>(() => loadFileListDensity());
  const setDensity = (next: FileListDensity) => {
    setDensityState(next);
    saveFileListDensity(next);
  };
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [handle, setHandle] = useState(user?.handle ?? '');
  const [profileIcon, setProfileIcon] = useState<ProfileIconId>(user?.icon ?? 'user');
  const [profileIconBackground, setProfileIconBackground] = useState<ProfileIconBackgroundId>(
    user?.iconBackground ?? 'jade',
  );
  const [profileAppearanceOpen, setProfileAppearanceOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [draftLabel, setDraftLabel] = useState('This Mac');
  const [draftIcon, setDraftIcon] = useState<PasskeyIcon>('fingerprint');
  const [editingPasskey, setEditingPasskey] = useState<PasskeyCredential>();
  const [editLabel, setEditLabel] = useState('');
  const [editIcon, setEditIcon] = useState<PasskeyIcon>('fingerprint');
  const [saving, setSaving] = useState(false);
  const [revokingId, setRevokingId] = useState<string>();
  const [enablingUnlockId, setEnablingUnlockId] = useState<string>();
  const showPasskeys = Boolean(user && !user.demo && passkeysSupported());
  const showProfile = Boolean(user && !user.demo);

  const devices = useQuery({
    queryKey: ['devices'],
    queryFn: () => api<{ devices: Device[] }>('/v1/devices'),
  });
  const storage = useQuery({
    queryKey: ['storage'],
    queryFn: () => api<StorageUsage>('/v1/storage'),
  });
  const passkeys = useQuery({
    queryKey: ['passkeys', workspace?.repositoryId],
    queryFn: () =>
      api<{ passkeys: PasskeyCredential[] }>(
        `/v1/passkeys${workspace?.repositoryId ? `?repositoryId=${encodeURIComponent(workspace.repositoryId)}` : ''}`,
      ),
    enabled: showPasskeys,
  });
  const audit = useQuery({
    queryKey: ['audit'],
    queryFn: () => api<{ events: AuditEvent[] }>('/v1/audit'),
  });

  const saveProfile = async () => {
    if (!firstName.trim() || !lastName.trim() || !handle.trim()) return;
    setSavingProfile(true);
    try {
      const result = await putJson<{ user: NonNullable<typeof user> }>('/v1/profile', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        handle: handle.trim().toLowerCase(),
        icon: profileIcon,
        iconBackground: profileIconBackground,
      });
      queryClient.setQueryData(['session'], { user: result.user });
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      toastManager.add({ title: 'Profile updated' });
    } catch (cause) {
      toastManager.add({
        title: 'Could not update profile',
        description: cause instanceof Error ? cause.message : 'Try again.',
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const openEnroll = () => {
    setDraftLabel('This Mac');
    setDraftIcon('fingerprint');
    setEnrollOpen(true);
  };

  const enroll = async () => {
    if (!draftLabel.trim()) return;
    setEnrolling(true);
    try {
      const result = await registerPasskey(
        draftLabel.trim(),
        draftIcon,
        workspace?.vaultMode === 'locked' && workspace.collectionKey.length > 0
          ? {
              repositoryId: workspace.repositoryId,
              collectionKey: Uint8Array.from(workspace.collectionKey),
            }
          : undefined,
      );
      await queryClient.invalidateQueries({ queryKey: ['passkeys'] });
      setEnrollOpen(false);
      toastManager.add({
        title: 'Passkey added',
        description: result.unlockEnabled
          ? 'It can sign you in and unlock this vault.'
          : 'It can sign you in. This passkey provider did not enable encrypted vault unlock.',
      });
    } catch (cause) {
      if (!(cause instanceof Error && cause.name === 'NotAllowedError')) {
        toastManager.add({
          title: 'Could not add passkey',
          description: cause instanceof Error ? cause.message : 'Try again.',
        });
      }
    } finally {
      setEnrolling(false);
    }
  };

  const startEdit = (passkey: PasskeyCredential) => {
    setEditingPasskey(passkey);
    setEditLabel(passkey.label);
    setEditIcon(passkey.icon || 'fingerprint');
  };

  const closeEdit = () => {
    setEditingPasskey(undefined);
  };

  const saveEdit = async () => {
    if (!editingPasskey || !editLabel.trim()) return;
    setSaving(true);
    try {
      await putJson(`/v1/passkeys/${editingPasskey.id}`, {
        label: editLabel.trim(),
        icon: editIcon,
      });
      await queryClient.invalidateQueries({ queryKey: ['passkeys'] });
      closeEdit();
      toastManager.add({ title: 'Passkey updated' });
    } catch (cause) {
      toastManager.add({
        title: 'Could not update passkey',
        description: cause instanceof Error ? cause.message : 'Try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id: string) => {
    setRevokingId(id);
    try {
      await revokePasskey(id);
      await queryClient.invalidateQueries({ queryKey: ['passkeys'] });
    } catch (cause) {
      toastManager.add({
        title: 'Could not remove passkey',
        description: cause instanceof Error ? cause.message : 'Try again.',
      });
    } finally {
      setRevokingId(undefined);
    }
  };

  const enableUnlock = async (passkey: PasskeyCredential) => {
    if (!workspace || workspace.vaultMode !== 'locked' || workspace.collectionKey.length === 0)
      return;
    setEnablingUnlockId(passkey.id);
    try {
      await enablePasskeyUnlock(passkey.id, {
        repositoryId: workspace.repositoryId,
        collectionKey: Uint8Array.from(workspace.collectionKey),
      });
      await queryClient.invalidateQueries({ queryKey: ['passkeys'] });
      toastManager.add({
        title: 'Vault unlock enabled',
        description: `${passkey.label} can now unlock this vault on a new browser.`,
      });
    } catch (cause) {
      if (!(cause instanceof Error && cause.name === 'NotAllowedError')) {
        toastManager.add({
          title: 'Could not enable vault unlock',
          description: cause instanceof Error ? cause.message : 'Try again.',
        });
      }
    } finally {
      setEnablingUnlockId(undefined);
    }
  };

  return (
    <div className={styles.page}>
      <Tabs
        defaultValue={showProfile ? 'profile' : 'workspace'}
        tabs={[
          ...(showProfile
            ? [
                {
                  value: 'profile',
                  label: 'Profile',
                  content: (
                    <div className={styles.section}>
                      <div className={styles.profilePreview}>
                        <ProfileAvatar
                          user={{
                            displayName:
                              `${firstName.trim()} ${lastName.trim()}`.trim() || user!.displayName,
                            icon: profileIcon,
                            iconBackground: profileIconBackground,
                          }}
                          size="md"
                        />
                        <div>
                          <div className={styles.rowTitle}>
                            {`${firstName.trim()} ${lastName.trim()}`.trim() || user!.displayName}
                          </div>
                          <div className={styles.rowMeta}>
                            {handle.trim() ? `@${handle.trim().toLowerCase()}` : user!.email}
                          </div>
                        </div>
                      </div>
                      <div className={styles.enrollForm}>
                        <div className={styles.nameRow}>
                          <TextField
                            label="First name"
                            autoComplete="given-name"
                            value={firstName}
                            onValueChange={setFirstName}
                          />
                          <TextField
                            label="Last name"
                            autoComplete="family-name"
                            value={lastName}
                            onValueChange={setLastName}
                          />
                        </div>
                        <TextField
                          label="Handle"
                          autoComplete="username"
                          spellCheck={false}
                          value={handle}
                          onValueChange={setHandle}
                        />
                        <div className={styles.profileAppearance}>
                          <div>
                            <div className={styles.prefLabel}>Profile appearance</div>
                            <p className={styles.prefHint}>Choose an icon and its background.</p>
                          </div>
                          <Popover
                            open={profileAppearanceOpen}
                            onOpenChange={setProfileAppearanceOpen}
                            trigger={
                              <Button
                                className={styles.appearanceTrigger}
                                aria-label="Customize profile appearance"
                              >
                                <ProfileAvatar
                                  user={{
                                    displayName:
                                      `${firstName.trim()} ${lastName.trim()}`.trim() ||
                                      user!.displayName,
                                    icon: profileIcon,
                                    iconBackground: profileIconBackground,
                                  }}
                                />
                                Customize
                              </Button>
                            }
                          >
                            <div className={styles.appearancePopover}>
                              <div>
                                <div className={styles.iconLabel}>Icon</div>
                                <div
                                  className={styles.iconGrid}
                                  role="radiogroup"
                                  aria-label="Profile icon"
                                >
                                  {PROFILE_ICON_OPTIONS.map(({ id, label, emoji }) => (
                                    <button
                                      key={id}
                                      type="button"
                                      role="radio"
                                      aria-checked={profileIcon === id}
                                      aria-label={label}
                                      className={`${styles.iconOption} ${profileIcon === id ? styles.iconOptionActive : ''}`}
                                      onClick={() => setProfileIcon(id)}
                                    >
                                      <span aria-hidden="true">{emoji}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <div className={styles.iconLabel}>Background</div>
                                <div
                                  className={styles.backgroundGrid}
                                  role="radiogroup"
                                  aria-label="Profile icon background"
                                >
                                  {PROFILE_ICON_BACKGROUND_OPTIONS.map(({ id, label, hex }) => (
                                    <button
                                      key={id}
                                      type="button"
                                      role="radio"
                                      aria-checked={profileIconBackground === id}
                                      aria-label={label}
                                      className={`${styles.backgroundOption} ${profileIconBackground === id ? styles.backgroundOptionActive : ''}`}
                                      style={{ backgroundColor: hex }}
                                      onClick={() => setProfileIconBackground(id)}
                                    />
                                  ))}
                                </div>
                              </div>
                            </div>
                          </Popover>
                        </div>
                        <Button
                          variant="primary"
                          disabled={
                            !firstName.trim() || !lastName.trim() || !handle.trim() || savingProfile
                          }
                          onClick={() => void saveProfile()}
                        >
                          {savingProfile ? 'Saving…' : 'Save profile'}
                        </Button>
                      </div>
                    </div>
                  ),
                },
              ]
            : []),
          {
            value: 'workspace',
            label: 'Workspace',
            content: (
              <div className={styles.prefList}>
                <div className={styles.prefRow}>
                  <div className={styles.prefText}>
                    <div className={styles.prefLabel}>Encrypted storage</div>
                    <p className={styles.prefHint}>
                      Cumulative ciphertext for this account, including history. Limit is 5 GB.
                    </p>
                  </div>
                  <StorageMeter usage={storage.data} />
                </div>
                <div className={styles.prefRow}>
                  <div className={styles.prefText}>
                    <div className={styles.prefLabel}>Queue changes before saving</div>
                    <p className={styles.prefHint}>
                      When queued, edits stay on this device until you press Save. Immediate mode
                      commits each change as its own encrypted snapshot.
                    </p>
                  </div>
                  <Checkbox
                    aria-label="Queue changes before saving"
                    checked={saveMode === 'queued'}
                    onCheckedChange={(checked) => {
                      const next: SaveMode = checked === true ? 'queued' : 'immediate';
                      if (next === 'immediate' && dirty) {
                        toastManager.add({
                          title: 'Unsaved changes still pending',
                          description: 'Save or discard them from the sidebar first.',
                        });
                        return;
                      }
                      setSaveMode(next);
                    }}
                  />
                </div>
                <div className={styles.prefRow}>
                  <div className={styles.prefText}>
                    <div className={styles.prefLabel}>File list density</div>
                    <p className={styles.prefHint}>Spacing for rows in the Files pane.</p>
                  </div>
                  <div className={styles.densityToggle} role="group" aria-label="List density">
                    <button
                      type="button"
                      className={`${styles.densityButton} ${density === 'comfy' ? styles.densityButtonActive : ''}`}
                      aria-pressed={density === 'comfy'}
                      aria-label="Comfy density"
                      title="Comfy"
                      onClick={() => setDensity('comfy')}
                    >
                      <Rows size={14} />
                    </button>
                    <button
                      type="button"
                      className={`${styles.densityButton} ${density === 'compact' ? styles.densityButtonActive : ''}`}
                      aria-pressed={density === 'compact'}
                      aria-label="Compact density"
                      title="Compact"
                      onClick={() => setDensity('compact')}
                    >
                      <List size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ),
          },
          {
            value: 'devices',
            label: 'Devices',
            content: (
              <div className={styles.section}>
                <div className={styles.connectCli}>
                  <div>
                    <div className={styles.prefLabel}>Connect a device</div>
                    <p className={styles.prefHint}>
                      Approve a new browser from its pairing QR, or run{' '}
                      <code className={styles.inlineCode}>trove auth login</code> for the CLI.
                    </p>
                  </div>
                  <Button variant="primary" onClick={() => void navigate({ to: '/connect' })}>
                    <TerminalWindow size={13} />
                    Connect device
                  </Button>
                </div>
                <div className={`${shared.list} ${styles.compactList}`}>
                  {devices.data?.devices.map((device) => (
                    <div className={`${shared.listRow} ${styles.compactRow}`} key={device.id}>
                      <div className={shared.listMain}>
                        <ProfileAvatar user={user!} />
                        <span className={styles.rowText}>
                          <span className={styles.rowTitle}>
                            {device.label}
                            <span className={styles.userName}>{user?.displayName}</span>
                          </span>
                          <span className={styles.rowMeta}>
                            {user?.email}
                            {' · '}
                            <RelativeTime value={device.lastSeenAt} />
                          </span>
                        </span>
                      </div>
                      {workspace?.deviceId === device.id ? (
                        <span className={`${shared.badge} ${shared.badgeGreen}`}>Current</span>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ),
          },
          ...(showPasskeys
            ? [
                {
                  value: 'passkeys',
                  label: 'Passkeys',
                  content: (
                    <div className={styles.section}>
                      <div className={styles.sectionHeader}>
                        <Button variant="primary" onClick={openEnroll}>
                          <Plus size={13} />
                          Add passkey
                        </Button>
                      </div>
                      <div className={`${shared.list} ${styles.compactList}`}>
                        {(passkeys.data?.passkeys.length ?? 0) === 0 ? (
                          <p className={shared.empty}>No passkeys yet.</p>
                        ) : (
                          passkeys.data?.passkeys.map((passkey) => {
                            const Icon = passkeyIconComponent(passkey.icon);
                            return (
                              <div
                                className={`${shared.listRow} ${styles.compactRow}`}
                                key={passkey.id}
                              >
                                <div className={shared.listMain}>
                                  <span className={styles.passkeyIcon}>
                                    <Icon size={14} />
                                  </span>
                                  <span className={styles.rowText}>
                                    <span className={styles.rowTitle}>
                                      {passkey.label}
                                      <span className={styles.userName}>{user?.displayName}</span>
                                    </span>
                                    <span className={styles.rowMeta}>
                                      {passkey.unlockEnabled ? 'Vault unlock on' : 'Sign-in only'} ·{' '}
                                      <RelativeTime value={passkey.lastUsedAt} />
                                    </span>
                                  </span>
                                </div>
                                <div className={styles.rowActions}>
                                  {!passkey.unlockEnabled && workspace?.vaultMode === 'locked' && (
                                    <Button
                                      aria-label={`Enable vault unlock for ${passkey.label}`}
                                      disabled={enablingUnlockId === passkey.id}
                                      onClick={() => void enableUnlock(passkey)}
                                    >
                                      <ShieldCheck size={13} />
                                      {enablingUnlockId === passkey.id
                                        ? 'Waiting…'
                                        : 'Enable unlock'}
                                    </Button>
                                  )}
                                  <Button
                                    aria-label={`Edit ${passkey.label}`}
                                    onClick={() => startEdit(passkey)}
                                  >
                                    <PencilSimple size={13} />
                                  </Button>
                                  <Button
                                    aria-label={`Remove ${passkey.label}`}
                                    disabled={revokingId === passkey.id}
                                    onClick={() => void revoke(passkey.id)}
                                  >
                                    <Trash size={13} />
                                  </Button>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  ),
                },
              ]
            : []),
          {
            value: 'activity',
            label: 'Access log',
            content: (
              <div className={`${shared.list} ${styles.compactList}`}>
                {audit.data?.events.map((event) => (
                  <div className={`${shared.listRow} ${styles.compactRow}`} key={event.id}>
                    <div className={shared.listMain}>
                      <span className={styles.passkeyIcon}>
                        <Pulse size={14} />
                      </span>
                      <span className={styles.rowText}>
                        <span className={styles.rowTitle}>{event.action.replace('.', ' ')}</span>
                        <span className={styles.rowMeta}>
                          {event.deviceLabel ? `${event.deviceLabel} · ` : ''}
                          <RelativeTime value={event.createdAt} />
                        </span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ),
          },
        ]}
      />

      <Dialog
        open={enrollOpen}
        onOpenChange={setEnrollOpen}
        title="Add passkey"
        actions={
          <>
            <Button onClick={() => setEnrollOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={enrolling || !draftLabel.trim()}
              onClick={() => void enroll()}
            >
              {enrolling ? 'Waiting…' : 'Continue'}
            </Button>
          </>
        }
      >
        <div className={styles.enrollForm}>
          <TextField
            label="Name"
            value={draftLabel}
            onValueChange={setDraftLabel}
            placeholder="MacBook · Touch ID"
          />
          <div>
            <div className={styles.iconLabel}>Icon</div>
            <div className={styles.iconGrid} role="radiogroup" aria-label="Passkey icon">
              {PASSKEY_ICON_OPTIONS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={draftIcon === id}
                  aria-label={label}
                  className={`${styles.iconOption} ${draftIcon === id ? styles.iconOptionActive : ''}`}
                  onClick={() => setDraftIcon(id)}
                >
                  <Icon size={16} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={Boolean(editingPasskey)}
        onOpenChange={(open) => {
          if (!open) closeEdit();
        }}
        title="Edit passkey"
        actions={
          <>
            <Button onClick={closeEdit}>Cancel</Button>
            <Button
              variant="primary"
              disabled={saving || !editLabel.trim()}
              onClick={() => void saveEdit()}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <div className={styles.enrollForm}>
          <TextField label="Name" value={editLabel} onValueChange={setEditLabel} />
          <div>
            <div className={styles.iconLabel}>Icon</div>
            <div className={styles.iconGrid} role="radiogroup" aria-label="Passkey icon">
              {PASSKEY_ICON_OPTIONS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={editIcon === id}
                  aria-label={label}
                  className={`${styles.iconOption} ${editIcon === id ? styles.iconOptionActive : ''}`}
                  onClick={() => setEditIcon(id)}
                >
                  <Icon size={16} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
